import styled from "@emotion/styled";
import format from "date-fns/format";
import { useSelector } from "react-redux";

import { SpacingToken } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { DateFormat } from "constants/date";
import { SubscriptionPhase } from "constants/subscription-phase";
import { calculatePrice } from "helpers/subscription-plans";
import { getNonViewersCount } from "store/entities/agents";
import { getRecurlyCoupon, getRecurlyNewCoupon } from "store/entities/recurly";
import {
  getCurrentSubscriptionDaysLeft,
  selectCurrentSubscription,
  selectSubscriptionPhase,
} from "store/entities/subscriptions";
import { withVar } from "themes/helpers";

import { useTaxChangePreview } from "../../components/SubscriptionCardForm/tax-preview/use-tax-change-preview";

import { PhaseExpired } from "./PhaseExpired";
import { PhasePaid } from "./PhasePaid";
import { PhasePaidNoCC } from "./PhasePaidNoCC";
import { PhaseTrialCC } from "./PhaseTrialCC";
import { PhaseTrialNoCC } from "./PhaseTrialNoCC";

export const CurrentPlanDetails = (): JSX.Element => {
  const subscription = useSelector(selectCurrentSubscription);
  const nonViewersCount = useSelector(getNonViewersCount);
  const newCoupon = useSelector(getRecurlyNewCoupon);
  const currentCoupon = useSelector(getRecurlyCoupon);

  const coupon = newCoupon || currentCoupon;

  // TODO: this hook could be moved to wrapper component then SubscriptionPlansDetails could be memoized
  const { taxChangePreview } = useTaxChangePreview();

  if (!subscription) {
    return (
      <Flex column>
        <PhaseContainer>
          <PhaseExpired />
        </PhaseContainer>
      </Flex>
    );
  }

  const price = calculatePrice(subscription, coupon);
  const phase = useSelector(selectSubscriptionPhase);
  const daysLeft = useSelector(getCurrentSubscriptionDaysLeft);
  const formattedExpirationTimestamp = format(subscription.subscriptionExpiresAt, DateFormat.MonthDayFullYear);

  return (
    <Flex column>
      <PhaseContainer>
        {phase === SubscriptionPhase.TrialNoCC && (
          <PhaseTrialNoCC nonViewersCount={nonViewersCount} daysLeft={daysLeft} coupon={coupon} />
        )}
        {phase === SubscriptionPhase.TrialCC && (
          <PhaseTrialCC
            nonViewersCount={nonViewersCount}
            price={price}
            isTaxed={!!taxChangePreview.currentTax}
            coupon={coupon}
            isCanceled={subscription.isCanceled}
            expirationDate={subscription.subscriptionExpiresAt}
          />
        )}
        {phase === SubscriptionPhase.PaidNoCC && (
          <PhasePaidNoCC
            nonViewersCount={nonViewersCount}
            isCanceled={subscription.isCanceled}
            expirationDate={formattedExpirationTimestamp}
            coupon={coupon}
          />
        )}
        {phase === SubscriptionPhase.Paid && (
          <PhasePaid
            price={price}
            isTaxed={!!taxChangePreview.currentTax}
            nonViewersCount={nonViewersCount}
            isCanceled={subscription.isCanceled}
            expirationDate={formattedExpirationTimestamp}
            coupon={coupon}
          />
        )}
        {(phase === SubscriptionPhase.ExpiredPaid || phase === SubscriptionPhase.ExpiredTrial) && (
          <PhaseExpired nonViewersCount={nonViewersCount} expirationDate={subscription.subscriptionExpiresAt} />
        )}
      </PhaseContainer>
    </Flex>
  );
};

const PhaseContainer = styled(Flex)`
  flex: 1;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  box-sizing: border-box;
  text-align: center;
  padding: ${withVar(SpacingToken.Spacing6)};
  gap: ${withVar(SpacingToken.Spacing1)};
`;
